import type { ReactNode } from 'react';
import { useGT } from 'gt-react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Separator } from 'src/components/ui/separator';

// The shape of an override history list, shared by the lead-time and
// inventory-policy panels: newest first, one row per override ever set, with
// the row chain itself as the history (an edited row shows as "replaced"
// rather than changing in place). Each list supplies its own headline and
// status vocabulary; the layout and the review flag live here so the two
// cannot drift. See docs/procurement/lead-time-overrides.md.

export function OverrideHistoryList({ children }: { children: ReactNode }) {
  return (
    <>
      <Separator className="my-2" />
      <ul className="space-y-1.5">{children}</ul>
    </>
  );
}

export function OverrideHistoryRow({
  headline,
  status,
  meta,
  deactivation,
  usage,
  actions,
}: {
  /** What the override does: the pinned days, or the fields it sets. */
  headline: ReactNode;
  status: ReactNode;
  /** Window · author · reason. */
  meta: string;
  /** Which dates this saved override actually wins. */
  usage?: ReactNode;
  /** Who revoked or replaced it; null while it is still live. */
  deactivation: string | null;
  /** Edit / revoke, or nothing once the row is no longer active. */
  actions: ReactNode;
}) {
  return (
    <li className="flex items-start justify-between gap-2 text-xs">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          {headline}
          {status}
        </div>
        <div className="text-gray-400">{meta}</div>
        {usage ? (
          <div className="mt-1 text-muted-foreground">{usage}</div>
        ) : null}
        {deactivation ? (
          <div className="text-gray-400">{deactivation}</div>
        ) : null}
      </div>
      {actions}
    </li>
  );
}

/** The row's status, plus the review flag every override list shows beside it. */
export function OverrideStatusBadges({
  label,
  className,
  filled,
  needsReview,
}: {
  label: string;
  /** Tone classes for the status badge. */
  className: string;
  /** True for a status that is actually applying, which reads as filled. */
  filled: boolean;
  needsReview: boolean;
}) {
  const gt = useGT();
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Badge variant={filled ? 'secondary' : 'outline'} className={className}>
        {label}
      </Badge>
      {needsReview ? (
        <Badge variant="secondary" className="bg-orange-100 text-orange-700">
          {gt('needs review', {
            context: 'procurement override status badge',
          })}
        </Badge>
      ) : null}
    </span>
  );
}

/** Edit / revoke for one active override row. */
export function OverrideRowActions<T>({
  override: o,
  scheduled = false,
  onEdit,
  onRequestRevoke,
}: {
  override: T;
  /**
   * True when this row's window has not started. The list judged that against
   * the clock it already read, and hands it to the revoke so the panel does
   * not have to read the clock again mid-render.
   */
  scheduled?: boolean;
  onEdit: (o: T) => void;
  onRequestRevoke: (o: T, scheduled: boolean) => void;
}) {
  const gt = useGT();
  return (
    <div className="flex shrink-0 items-center">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs"
        onClick={() => onEdit(o)}
      >
        {gt('Edit', { context: 'procurement override edit button' })}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs text-red-600"
        onClick={() => onRequestRevoke(o, scheduled)}
      >
        {gt('Revoke', { context: 'procurement override revoke button' })}
      </Button>
    </div>
  );
}

/** Retired overrides stay available without obscuring the current schedule. */
export function OverrideHistoryArchive({
  count,
  children,
}: {
  count: number;
  children: ReactNode;
}) {
  const gt = useGT();
  if (count === 0) return null;
  return (
    <details className="mt-3 text-xs">
      <summary className="cursor-pointer font-medium">
        {gt('History ({n})', {
          context: 'procurement retired override history disclosure',
          n: count,
        })}
      </summary>
      <OverrideHistoryList>{children}</OverrideHistoryList>
    </details>
  );
}
